import React from 'react';
import { Shield, Lock, Eye, Database, Users, Bell, Globe, FileText } from 'lucide-react';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';

const PrivacyPolicy = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
            <div className="absolute transform -rotate-12 right-0 top-40 w-72 h-72 bg-white rounded-full"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Shield className="w-4 h-4" />
                <span className="text-sm font-medium">Your Privacy Matters</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                Privacy Policy
              </h1>
              <p className="text-lg md:text-xl text-indigo-100 max-w-2xl mx-auto">
                How we collect, use, and protect your personal and medical information
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
            <p className="text-gray-600 text-sm">
              <strong>Effective Date:</strong> January 1, 2025
            </p>

            <p className="text-gray-700 leading-relaxed">
              At <strong>HomeCure</strong>, a brand of <strong>Sanghmitra Healthcare Private Limited</strong>, we are committed to protecting your privacy and safeguarding your personal and medical information. This Privacy Policy explains what information we collect, how we use it, who we share it with, and your rights regarding your data.
            </p>

            <p className="text-gray-700 leading-relaxed">
              By using our Platform (website, mobile app, or services), you consent to the collection and use of information as described in this Privacy Policy.
            </p>

            {/* Section 1 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">1. INFORMATION WE COLLECT</h2>
              </div>
              <div className="pl-13 space-y-4 text-gray-700 leading-relaxed">
                <p><strong>1.1 Personal Information</strong></p>
                <p>When you register or use our services, we collect:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Identity Information:</strong> Full name, date of birth, gender, age</li>
                  <li><strong>Contact Information:</strong> Phone number, email address, residential address</li>
                  <li><strong>Login Credentials:</strong> Password, OTP, security questions</li>
                  <li><strong>Government IDs:</strong> Aadhaar, PAN (if required for verification or insurance claims)</li>
                </ul>

                <p className="pt-3"><strong>1.2 Medical & Health Information</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Medical History:</strong> Past illnesses, surgeries, allergies, chronic conditions</li>
                  <li><strong>Prescriptions:</strong> Uploaded prescriptions from doctors</li>
                  <li><strong>Lab Reports:</strong> Diagnostic test results, imaging reports</li>
                  <li><strong>Consultation Notes:</strong> Symptoms, diagnoses, treatment plans shared during consultations</li>
                  <li><strong>Vital Signs:</strong> Height, weight, blood pressure, blood sugar levels (if provided)</li>
                </ul>

                <p className="pt-3"><strong>1.3 Transaction Information</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Payment Details:</strong> Card type, last 4 digits, UPI ID, billing address (full card details not stored)</li>
                  <li><strong>Order History:</strong> Medicines purchased, tests booked, consultations availed</li>
                  <li><strong>Invoice & Receipts:</strong> Transaction records for services and products</li>
                </ul>

                <p className="pt-3"><strong>1.4 Device & Usage Information</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Device Data:</strong> Device type, operating system, browser type, IP address, device ID</li>
                  <li><strong>Location Data:</strong> GPS location (if you enable location services) for delivery and ambulance services</li>
                  <li><strong>Usage Data:</strong> Pages visited, time spent, features used, click patterns</li>
                  <li><strong>Cookies:</strong> Small files stored on your device to improve user experience</li>
                </ul>

                <p className="pt-3"><strong>1.5 Communication Data</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Customer support interactions (calls, emails, chat)</li>
                  <li>Feedback, reviews, and ratings</li>
                  <li>Notifications and alerts you receive</li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">2. HOW WE USE YOUR INFORMATION</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>We use your information for the following purposes:</p>

                <p><strong>2.1 Service Delivery</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Facilitate doctor consultations, lab tests, medicine delivery, and paramedical services</li>
                  <li>Share your medical information with healthcare providers to enable consultations and treatment</li>
                  <li>Process payments and issue invoices</li>
                  <li>Deliver products to your address</li>
                  <li>Dispatch ambulances or home healthcare services</li>
                </ul>

                <p><strong>2.2 Account Management</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Create and manage your account</li>
                  <li>Authenticate and verify your identity</li>
                  <li>Send OTPs for login and transactions</li>
                </ul>

                <p><strong>2.3 Communication</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Send booking confirmations, reminders, and updates via SMS, email, or in-app notifications</li>
                  <li>Respond to customer support queries and complaints</li>
                  <li>Send promotional offers, discounts, and health tips (you can opt-out anytime)</li>
                </ul>

                <p><strong>2.4 Personalization & Improvement</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Personalize your experience based on your preferences and usage</li>
                  <li>Recommend relevant services, products, and healthcare providers</li>
                  <li>Analyze usage patterns to improve our Platform and services</li>
                </ul>

                <p><strong>2.5 Legal & Regulatory Compliance</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Comply with legal obligations and regulations</li>
                  <li>Prevent fraud, abuse, and misuse of the Platform</li>
                  <li>Respond to legal requests, court orders, or government inquiries</li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">3. INFORMATION SHARING & DISCLOSURE</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>We do <strong>not sell</strong> your personal or medical information to third parties. However, we may share your information with:</p>

                <p><strong>3.1 Healthcare Providers</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Doctors, diagnostic labs, pharmacies, and paramedical service providers you book through our Platform</li>
                  <li>Sharing is limited to information necessary for service delivery</li>
                  <li>Providers are bound by confidentiality and professional ethics</li>
                </ul>

                <p><strong>3.2 Service Partners</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Delivery Partners:</strong> For medicine and product delivery</li>
                  <li><strong>Payment Gateways:</strong> To process secure payments</li>
                  <li><strong>SMS/Email Providers:</strong> For sending notifications and alerts</li>
                  <li><strong>Cloud Storage Providers:</strong> For secure data storage and backup</li>
                </ul>

                <p><strong>3.3 Legal Authorities</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>If required by law, court order, or government authority</li>
                  <li>To protect our rights, safety, or property</li>
                  <li>To prevent fraud or illegal activity</li>
                </ul>

                <p><strong>3.4 Business Transfers</strong></p>
                <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity, subject to the same privacy protections.</p>

                <p><strong>3.5 With Your Consent</strong></p>
                <p>We may share your information with third parties if you explicitly consent (e.g., sharing reports with family members).</p>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">4. DATA SECURITY</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>We take data security seriously and implement industry-standard measures to protect your information:</p>

                <p><strong>4.1 Technical Safeguards</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Encryption:</strong> Data is encrypted in transit (SSL/TLS) and at rest</li>
                  <li><strong>Secure Servers:</strong> Hosted on secure cloud infrastructure with firewalls and intrusion detection</li>
                  <li><strong>Access Controls:</strong> Role-based access for employees; minimal access to sensitive data</li>
                  <li><strong>Regular Audits:</strong> Security assessments and vulnerability testing</li>
                </ul>

                <p><strong>4.2 Organizational Safeguards</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Employee training on data privacy and security</li>
                  <li>Confidentiality agreements with staff and partners</li>
                  <li>Incident response plan for data breaches</li>
                </ul>

                <p><strong>4.3 Your Responsibility</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Keep your password secure and do not share it</li>
                  <li>Log out of your account after use, especially on shared devices</li>
                  <li>Report any unauthorized access immediately</li>
                </ul>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">5. DATA RETENTION</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>5.1 Retention Period</strong></p>
                <p>We retain your information for as long as necessary to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide services and maintain your account</li>
                  <li>Comply with legal and regulatory requirements (e.g., tax records, medical records)</li>
                  <li>Resolve disputes and enforce agreements</li>
                </ul>

                <p><strong>5.2 Medical Records</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Prescriptions, lab reports, and consultation notes are retained for a minimum of 5 years (as per applicable laws)</li>
                  <li>You may request deletion after this period, subject to legal requirements</li>
                </ul>

                <p><strong>5.3 Account Deletion</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>If you delete your account, personal data will be anonymized or deleted within 90 days</li>
                  <li>Some information may be retained for legal compliance (e.g., transaction records)</li>
                </ul>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">6. YOUR RIGHTS & CHOICES</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>You have the following rights regarding your personal information:</p>

                <p><strong>6.1 Access & Correction</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access your personal and medical data anytime through your account</li>
                  <li>Update or correct inaccurate information</li>
                </ul>

                <p><strong>6.2 Data Portability</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Request a copy of your data in a portable format (e.g., PDF, CSV)</li>
                </ul>

                <p><strong>6.3 Deletion</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Request deletion of your account and personal data</li>
                  <li>Some data may be retained for legal compliance</li>
                </ul>

                <p><strong>6.4 Opt-Out of Marketing</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Unsubscribe from promotional emails and SMS by clicking "Unsubscribe"</li>
                  <li>Disable push notifications in app settings</li>
                </ul>

                <p><strong>6.5 Withdraw Consent</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Withdraw consent for data processing (may limit access to certain services)</li>
                </ul>

                <p><strong>6.6 Lodge a Complaint</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Contact our Data Protection Officer (DPO) or file a complaint with relevant authorities</li>
                </ul>

                <p className="pt-3">
                  To exercise these rights, contact us at: <strong>privacy@homecure.com</strong>
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">7. COOKIES & TRACKING TECHNOLOGIES</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>7.1 What are Cookies?</strong></p>
                <p>Cookies are small text files stored on your device to enhance your browsing experience.</p>

                <p><strong>7.2 Types of Cookies We Use</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Essential Cookies:</strong> Required for Platform functionality (login, cart)</li>
                  <li><strong>Performance Cookies:</strong> Analyze usage and improve performance</li>
                  <li><strong>Functional Cookies:</strong> Remember preferences and settings</li>
                  <li><strong>Advertising Cookies:</strong> Deliver relevant ads (third-party)</li>
                </ul>

                <p><strong>7.3 Managing Cookies</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You can disable cookies in your browser settings</li>
                  <li>Note: Disabling cookies may affect Platform functionality</li>
                </ul>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">8. CHILDREN'S PRIVACY</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>Our Platform is not intended for users under the age of 18 without parental consent.</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>If you are booking services for a minor, you represent that you have legal authority to do so</li>
                  <li>We do not knowingly collect data from children under 18 without parental consent</li>
                  <li>If we learn that we have inadvertently collected such data, we will delete it promptly</li>
                </ul>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">9. THIRD-PARTY LINKS</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>Our Platform may contain links to third-party websites (e.g., payment gateways, social media).</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We are not responsible for the privacy practices of these third parties</li>
                  <li>We encourage you to read their privacy policies before providing any information</li>
                </ul>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">10. CHANGES TO THIS PRIVACY POLICY</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or business operations.</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>We will notify you of significant changes via email, in-app notification, or a notice on the Platform</li>
                  <li>The "Effective Date" at the top will reflect the latest update</li>
                  <li>Continued use of the Platform after changes constitutes acceptance of the updated policy</li>
                </ul>
              </div>
            </section>

            {/* Section 11 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">11. CONTACT US</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your data, please contact us:</p>
                <div className="bg-gray-50 rounded-lg p-4 mt-3">
                  <p><strong>Sanghmitra Healthcare Private Limited</strong></p>
                  <p><strong>Brand:</strong> HomeCure</p>
                  <p>📧 Email: Homecureexpress@gmail.com</p>
                  <p>📧 Privacy: privacy@homecure.com</p>
                  <p>📧 Support: support@homecure.com</p>
                  <p>📞 Phone: +91 98765 43210</p>
                  <p>📍 Address: Patna, Bihar, India</p>
                  <p>🌐 Website: www.homecureexpress.com</p>
                  <p className="pt-2"><strong>Data Protection Officer (DPO):</strong> dpo@homecure.com</p>
                </div>
              </div>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500">
                <strong>Last Updated:</strong> January 1, 2025
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Thank you for trusting Sanghmitra Healthcare Private Limited (HomeCure) with your healthcare needs and personal information. We are committed to protecting your privacy and providing transparent, secure services.</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
